import styles from './styles.module.scss';
import Typography from '@components/common/typography';
import Button from '@components/common/button';
import { useLocales } from '@hooks/helpers/useLocales';
import { motion } from 'framer-motion';
import { pageAnimation } from '@const/animation';
import { useNavigationContext } from '@hooks/context/useNavigationContext';
import linkIllustration from '@svg/illustrations/invite_link.svg';
import emailIllustration from '@svg/illustrations/invite_email.svg';
import InfoBlock from '@components/common/info_block';
import FormField from '@components/form_field';
import Input from '@components/common/input';
import { useForm } from '@hooks/helpers/useForm';
import { FormProvider } from 'react-hook-form';
import { apiUserService } from '@api';
import { useApiMutation } from '@hooks/api/useApiMutation';
import { useUserContext } from '@hooks/context/useUserContext';
import { Icon } from '@components/icons';
import { copyStringToClipboard } from '@utils/index';

const InviteExternalSection = () => {
    const form = useForm({
        defaultValues: {
            first_name: '',
            last_name: '',
            email: '',
            phone: ''
        }
    });
    const { submitLocale, validationLocale, requestSuccessLocale, requestErrorLocale } =
        useLocales();
    const { handleBack } = useNavigationContext();
    const { user, loyalty } = useUserContext();

    const { handleRequest: handleSendInvite, isLoading: isSendInviteLoading } = useApiMutation({
        method: () => {
            const values = form.getValues();
            return apiUserService.sendInvite({
                first_name: values.first_name,
                last_name: values.last_name,
                email: values.email || '',
                phone: values.phone
            });
        },
        onSuccess: () => {
            requestSuccessLocale('contact_invited');
            form.reset();
        },
        onError: requestErrorLocale
    });

    const onSubmit = () => {
        form.trigger().then((isValid) => {
            if (isValid) {
                handleSendInvite(undefined);
            }
        });
    };

    return (
        <motion.div {...pageAnimation} className={styles.wrapper}>
            <div className={styles.header}>
                <Button
                    className={styles.back}
                    variant="text-gray"
                    leftIcon="arrow_left"
                    onClick={handleBack}>
                    {submitLocale('back')}
                </Button>
                <Typography variant="h4">Invite to XEPPT</Typography>
            </div>
            <Typography variant="body3" className={styles.page_description}>
                Invite your friends to XEPPT by sharing your unique invite link or sending an invite
                email — earn <span>{loyalty?.invite_sender_points || 0} points</span> for every
                successful referral
            </Typography>
            <div className={styles.content}>
                <div className={styles.invite_link}>
                    <img src={linkIllustration} alt="" />
                    <Typography weight="semibold" variant="body1" className={styles.title}>
                        Share your invite link
                    </Typography>
                    <Typography variant="body3" alignment="center" className={styles.description}>
                        Send this invite link to a friend and make sure they sign up through it
                    </Typography>
                    <div className={styles.link}>
                        <Typography variant="body3">{`${window.location.origin}/?ref=${user?.referral || ''}`}</Typography>
                        <button onClick={() => copyStringToClipboard(`${window.location.origin}/?ref=${user?.referral || ''}`)}>
                            <Icon name="copy" />
                        </button>
                    </div>
                </div>
                <div className={styles.divider}>
                    <span>or</span>
                </div>
                <div className={styles.invite_email}>
                    <img src={emailIllustration} alt="" />
                    <Typography weight="semibold" variant="body1" className={styles.title}>
                        Send invite via email
                    </Typography>
                    <Typography variant="body3" alignment="center" className={styles.description}>
                        We'll send your friend an invite by email with your invite link included
                    </Typography>
                    <FormProvider {...form}>
                        <div className={styles.form}>
                            <div className={styles.add_external}>
                                <FormField
                                    name="first_name"
                                    renderComponent={(props) => (
                                        <Input
                                            label="Recipient’s first name"
                                            full
                                            {...props}
                                            placeholder="Enter the recipient's first name"
                                        />
                                    )}
                                    rules={{
                                        required: {
                                            value: true,
                                            message: validationLocale('first_name')
                                        }
                                    }}
                                />
                                <FormField
                                    name="last_name"
                                    renderComponent={(props) => (
                                        <Input
                                            label="Recipient’s last name"
                                            full
                                            {...props}
                                            placeholder="Enter your the recipient's last name"
                                        />
                                    )}
                                    rules={{
                                        required: {
                                            value: true,
                                            message: validationLocale('last_name')
                                        }
                                    }}
                                />
                                <FormField
                                    name="email"
                                    renderComponent={(props) => (
                                        <Input
                                            label="Recipient’s email"
                                            full
                                            {...props}
                                            placeholder="Enter the recipient's email"
                                        />
                                    )}
                                    rules={{
                                        required: {
                                            value: true,
                                            message: validationLocale('email')
                                        }
                                    }}
                                />
                                <FormField
                                    name="phone"
                                    renderComponent={(props) => (
                                        <Input
                                            label="Recipient’s phone number (optional)"
                                            full
                                            {...props}
                                            mask="1 (999) 999-9999"
                                            placeholder="Enter the recipient's phone number"
                                        />
                                    )}
                                />
                            </div>
                        </div>
                    </FormProvider>
                    <InfoBlock variant="gray">
                        By sending an invite email, you accept that some of your details will be
                        shared.
                    </InfoBlock>
                    <Button
                        variant="primary"
                        rightIcon="arrow_right"
                        onClick={onSubmit}
                        isLoading={isSendInviteLoading}>
                        Send invite
                    </Button>
                </div>
            </div>
        </motion.div>
    );
};

export default InviteExternalSection;
